import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CreditCard, Check, AlertTriangle, ExternalLink, ShieldCheck } from "lucide-react";

const TONE = {
  ready: "border-emerald-200 bg-emerald-50",
  pending: "border-amber-200 bg-amber-50",
  incomplete: "border-amber-200 bg-amber-50",
  not_started: "border-border bg-card",
};

/** Stripe Connect Express payout setup. MCC never sees or stores bank details. */
export default function PayoutsPanel({ compact = false }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ledger, setLedger] = useState(null);

  const load = async (refresh = false) => {
    const { data } = await api.get("/payouts/status", { params: { refresh } });
    setStatus(data);
    if (!compact) api.get("/payouts/ledger").then((r) => setLedger(r.data)).catch(() => {});
  };
  useEffect(() => {
    const returned = window.location.pathname.includes("/payouts/return");
    load(returned);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payouts/onboarding-link", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  if (!status) return <Loader2 className="h-6 w-6 animate-spin text-accent" />;
  const ready = status.payouts_enabled;
  const due = status.requirements?.currently_due || [];

  return (
    <div className="space-y-5 max-w-2xl" data-testid="payouts-panel">
      <div className={`rounded-2xl border p-5 ${TONE[status.state] || TONE.not_started}`} data-testid="payouts-status-card">
        <p className="font-display font-semibold inline-flex items-center gap-2">
          {ready ? <Check className="h-4 w-4 text-emerald-600" /> : <CreditCard className="h-4 w-4 text-accent" />}
          {ready ? "Set up your payouts 💳" : "Set up your payouts 💳"}
        </p>
        <p className="text-sm mt-2">
          Connect your payment account now so there are no delays receiving earnings from your
          My Clean Connect™ bookings. Your payment and bank details are securely handled by Stripe.
        </p>
        <p className="text-sm font-medium mt-3" data-testid="payouts-state">
          Status: <span className={ready ? "text-emerald-700" : "text-amber-700"}>{status.label}</span>
        </p>
        {due.length > 0 && (
          <p className="text-xs text-muted-foreground mt-1" data-testid="payouts-requirements">
            Stripe still needs: {due.join(", ").replace(/_/g, " ")}
          </p>
        )}
        {!ready && status.required_to_accept && (
          <p className="text-sm mt-3 inline-flex items-start gap-2" data-testid="payouts-gate-note">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            You can finish registration without this, but you can't accept a paid booking until
            your payouts are ready.
          </p>
        )}
        <div className="flex flex-wrap gap-3 mt-4">
          <button onClick={start} disabled={busy}
            className="px-6 py-3 rounded-full bg-accent text-white font-medium inline-flex items-center gap-2 disabled:opacity-50 hover:bg-accent/90 transition-colors"
            data-testid="payouts-setup-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
            {status.connected ? (ready ? "Manage payout details" : "Continue payout setup") : "Set up payouts"}
          </button>
          {status.connected && (
            <button onClick={() => load(true)} className="px-5 py-3 rounded-full border border-border font-medium hover:bg-secondary transition-colors" data-testid="payouts-refresh-btn">
              Refresh status
            </button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-3 inline-flex items-start gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          My Clean Connect™ never sees or stores your bank details — Stripe collects and holds them.
        </p>
      </div>

      {!compact && (
        <div className="bg-card rounded-2xl border border-primary/5 p-5" data-testid="payouts-ledger">
          <p className="text-sm font-semibold">Payments sent to you</p>
          {!ledger || ledger.transfers.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-2">
              No payments yet. Your earnings are sent to your connected account automatically after
              each completed job.
            </p>
          ) : (
            <div className="mt-3 divide-y divide-border">
              {ledger.transfers.map((t) => (
                <div key={t.transfer_id} className="flex justify-between py-2.5 text-sm">
                  <span className="text-muted-foreground">{new Date(t.created_at).toLocaleDateString("en-GB")}</span>
                  <span className="font-medium">£{t.amount.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between py-2.5 text-sm font-semibold">
                <span>Total paid</span><span data-testid="payouts-total">£{ledger.total_paid.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
